import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { modulesClient } from '../apis/client-instances';
import { alert } from './function';

export function useModuleManagement(courseId: number) {
  const [modules, setModules] = useState<any[]>([]);
  const [showModuleDialog, setShowModuleDialog] = useState(false);
  const [moduleToEdit, setModuleToEdit] = useState<any>(null);
  const [confirmModuleOpen, setConfirmModuleOpen] = useState(false);
  const [deleteModuleId, setDeleteModuleId] = useState<number | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const fetchModules = async () => {
    // Skip fetch if courseId is invalid (0 or negative)
    if (!courseId || courseId <= 0) {
      return;
    }
    try {
      const mods = await modulesClient.getModulesByCourse(courseId);
      setModules(mods || []);
    } catch (error) {
      console.error('Failed to fetch modules:', error);
      setModules([]);
    }
  };

  // Seed modules from the course detail payload (course.modules) so the player
  // gets the curriculum in one round trip instead of a separate fetch that
  // races the lesson load.
  const seedModules = (mods: any[]) => {
    setModules(mods ?? []);
  };

  const openAddModuleDialog = () => {
    setModuleToEdit(null);
    setShowModuleDialog(true);
  };

  const openEditModuleDialog = (module: any) => {
    setModuleToEdit(module);
    setShowModuleDialog(true);
  };

  const closeModuleDialog = () => {
    setShowModuleDialog(false);
    setModuleToEdit(null);
  };

  const saveModule = async (moduleData: { title: string; description?: string }) => {
    try {
      if (moduleToEdit) {
        // Update existing module
        const updated = await modulesClient.updateModule(moduleToEdit.id, moduleData);
        setModules((prev) => prev.map((m) => (m.id === moduleToEdit.id ? updated : m)));
        setModuleToEdit(null);
      } else {
        // Create new module
        const newModule = await modulesClient.createModule(courseId, moduleData);
        setModules((prev) => [...prev, newModule]);
      }
      alert(enqueueSnackbar, 'Section saved successfully', 'success');
      closeModuleDialog();
    } catch (error) {
      alert(enqueueSnackbar, 'Failed to save section', 'error');
      throw error;
    }
  };

  const confirmDeleteModule = (moduleId: number) => {
    setDeleteModuleId(moduleId);
    setConfirmModuleOpen(true);
  };

  const deleteModule = async () => {
    setConfirmModuleOpen(false);
    if (deleteModuleId) {
      try {
        await modulesClient.deleteModule(deleteModuleId);
        setModules((prev) => prev.filter((m) => m.id !== deleteModuleId));
        alert(enqueueSnackbar, 'Section deleted. Lessons are now ungrouped.', 'success');
      } catch (error) {
        console.error('Failed to delete module:', error);
        alert(enqueueSnackbar, 'Failed to delete section', 'error');
      }
    }
    setDeleteModuleId(null);
  };

  const cancelDeleteModule = () => {
    setConfirmModuleOpen(false);
    setDeleteModuleId(null);
  };

  const reorderModules = async (moduleIdsInOrder: number[]) => {
    try {
      await modulesClient.reorderModules(courseId, moduleIdsInOrder);
      // Optimistically update local state
      const reorderedModules = moduleIdsInOrder
        .map((id) => modules.find((m) => m.id === id))
        .filter((m) => m != null);
      setModules(reorderedModules);
      alert(enqueueSnackbar, 'Sections reordered successfully', 'success');
    } catch (error) {
      console.error('Failed to reorder modules:', error);
      alert(enqueueSnackbar, 'Failed to reorder sections', 'error');
      // Refresh modules to revert optimistic update
      fetchModules();
    }
  };

  return {
    modules,
    fetchModules,
    seedModules,
    // Module dialog state
    showModuleDialog,
    moduleToEdit,
    openAddModuleDialog,
    openEditModuleDialog,
    closeModuleDialog,
    saveModule,
    // Module delete confirmation
    confirmModuleOpen,
    confirmDeleteModule,
    deleteModule,
    cancelDeleteModule,
    // Module reordering
    reorderModules,
  };
}
